/**
 * State layer for the shared member-action confirmation, paired with
 * UserActionsController. Split per the MVC convention: state mutation lives in
 * the delegate, orchestration in the controller.
 *
 * Owns exactly one key, `userActionConfirm`. A modal is a page-level SINGLETON
 * on both consuming surfaces — only one can be open, whether the page renders
 * one member or five hundred rows — so the identity of the target lives INSIDE
 * the confirm object rather than in a per-clientId map. That is what lets the
 * Reports table and the single-member /users/user page share this slice without
 * either changing its own state shape.
 *
 * Mixed in alongside its controller:
 *   Inherit(ClockoutController, UserActionsDelegate, UserActionsController, ...)
 *
 * NO class fields, NO constructor. `Inherit` copies a mixin's instance
 * properties onto the host with defineProperty, so a class field would become an
 * own property shadowing the host's own methods; and the host's `state = {...}`
 * initializes AFTER mixins are copied, so a declared state would be wiped
 * anyway. Prototype methods and getters only.
 */
class UserActionsDelegate {
    /**
     * The open confirmation, or null. Defaulted rather than initialized: the
     * host's `state = {...}` runs after mixins are copied, so this must not
     * depend on the host declaring the key.
     * @returns {{action: string, clientId: string, name?: string, submitting: boolean}|null}
     */
    get userActionConfirm() {
        return this.state?.userActionConfirm ?? null;
    }

    /**
     * Merge into the open confirmation, or clear it with null. Functional
     * setState so a cancel racing a confirm cannot read a stale slice.
     * @param {object|null} patch
     */
    setUserActionConfirm(patch) {
        this.setState((prev) => ({
            userActionConfirm: patch === null ? null : { ...(prev.userActionConfirm || {}), ...patch },
        }));
    }
}

export default UserActionsDelegate;
